import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  CalendarIcon,
  UserIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { reportsAPI, employeeAPI, departmentsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Reports = () => {
  const [activeTab, setActiveTab] = useState('attendance');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Attendance Report Filters
  const [attendanceFilters, setAttendanceFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    department: '',
    employee_id: '',
    status: ''
  });

  // Early Check-in Report Filters  
  const [earlyCheckinFilters, setEarlyCheckinFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    department: '',
    employee_id: ''
  });

  const { user, isEmployee, isManager, isAdmin } = useAuth();

  // Tab configuration
  const tabs = [
    {
      id: 'attendance',
      name: 'Attendance Report',
      icon: ClockIcon,
      description: 'Employee attendance and time tracking',
      available: ['admin', 'manager', 'employee']
    },
    {
      id: 'early-checkin',
      name: 'Early Check-in Report',
      icon: ExclamationTriangleIcon,
      description: 'Employees who check in before official start time',
      available: ['admin', 'manager']
    },
    {
      id: 'payroll',
      name: 'Payroll Report',
      icon: DocumentArrowDownIcon,
      description: 'Payroll summaries and analysis',
      available: ['admin', 'manager']
    },
    {
      id: 'leave',
      name: 'Leave Report',
      icon: CalendarIcon,
      description: 'Leave requests and balances',
      available: ['admin', 'manager', 'employee']
    },
    {
      id: 'employee',
      name: 'Employee Report',
      icon: UserIcon,
      description: 'Employee demographics and statistics',
      available: ['admin', 'manager']
    }
  ];

  const availableTabs = tabs.filter(tab => {
    if (isAdmin()) return true;
    if (isManager()) return tab.available.includes('manager');
    if (isEmployee()) return tab.available.includes('employee');
    return false;
  });

  // Fetch employees and departments for filters
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Fetch employees using employee API
        const employeesResponse = await employeeAPI.getAll({});
        if (employeesResponse?.data?.employees) {
          setEmployees(employeesResponse.data.employees);
        }

        // Fetch departments using departments API
        const deptResponse = await departmentsAPI.getActive();
        setDepartments(deptResponse.data.departments || []);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      }
    };

    fetchFilterData();
  }, []);

  const fetchAttendanceReport = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = { ...attendanceFilters };
      
      // For employees, limit to their own data
      if (isEmployee()) {
        params.employee_id = user.employee_id || user.id;
      }

      const response = await reportsAPI.getAttendance(params);
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching attendance report:', error);
      setError(error.response?.data?.message || 'Failed to fetch attendance report');
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (format) => {
    try {
      const params = { ...attendanceFilters };
      if (isEmployee()) {
        params.employee_id = user.employee_id || user.id;
      }

      const response = await reportsAPI.export('attendance', format, params);
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'csv' ? 'text/csv' : 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report');
    }
  };

  // Helper function to format time display
  const formatTimeDisplay = (timeValue) => {
    if (!timeValue || timeValue === null || timeValue === undefined) return 'N/A';
    
    // Handle time strings (HH:MM:SS) from database
    if (typeof timeValue === 'string' && timeValue.includes(':')) {
      return timeValue.substring(0, 5); // Return HH:MM format
    }
    
    // Handle full datetime strings
    try {
      return new Date(timeValue).toLocaleTimeString('en-GB', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } catch (error) {
      return 'N/A';
    }
  };

  const calculateHours = (clockIn, clockOut) => {
    if (!clockIn || !clockOut) return 0;
    
    // Handle time strings (HH:MM:SS) from database
    if (typeof clockIn === 'string' && clockIn.includes(':')) {
      const [hours1, minutes1] = clockIn.split(':').map(Number);
      const [hours2, minutes2] = clockOut.split(':').map(Number);
      
      const totalMinutes1 = hours1 * 60 + minutes1;
      const totalMinutes2 = hours2 * 60 + minutes2;
      
      const diffMinutes = totalMinutes2 - totalMinutes1;
      return (diffMinutes / 60).toFixed(2);
    }
    
    // Handle full datetime strings
    const start = new Date(clockIn);
    const end = new Date(clockOut);
    return ((end - start) / (1000 * 60 * 60)).toFixed(2);
  };

  const calculateExtraHours = (totalHours, standardHours = 8) => {
    const hours = parseFloat(totalHours);
    return hours > standardHours ? (hours - standardHours).toFixed(2) : 0;
  };

  const renderAttendanceReport = () => {
    if (!reportData) return null;

    const attendanceRecords = reportData.attendance_data || reportData.attendance_records || [];
    
    // Calculate summary statistics
    const summary = {
      present_days: attendanceRecords.filter(r => r.status === 'present').length,
      absent_days: attendanceRecords.filter(r => r.status === 'absent').length,
      late_days: attendanceRecords.filter(r => r.status === 'late').length,
      total_records: attendanceRecords.length,
      total_hours: attendanceRecords.reduce((sum, r) => sum + parseFloat(calculateHours(r.clock_in, r.clock_out)), 0).toFixed(2),
      extra_hours: attendanceRecords.reduce((sum, r) => sum + parseFloat(calculateExtraHours(calculateHours(r.clock_in, r.clock_out))), 0).toFixed(2)
    };

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="card bg-green-50 border-green-200 p-4">
            <div className="text-sm font-medium text-green-800">Present Days</div>
            <div className="text-2xl font-bold text-green-900">{summary.present_days}</div>
          </div>
          <div className="card bg-red-50 border-red-200 p-4">
            <div className="text-sm font-medium text-red-800">Absent Days</div>
            <div className="text-2xl font-bold text-red-900">{summary.absent_days}</div>
          </div>
          <div className="card bg-yellow-50 border-yellow-200 p-4">
            <div className="text-sm font-medium text-yellow-800">Late Days</div>
            <div className="text-2xl font-bold text-yellow-900">{summary.late_days}</div>
          </div>
          <div className="card bg-blue-50 border-blue-200 p-4">
            <div className="text-sm font-medium text-blue-800">Total Records</div>
            <div className="text-2xl font-bold text-blue-900">{summary.total_records}</div>
          </div>
          <div className="card bg-purple-50 border-purple-200 p-4">
            <div className="text-sm font-medium text-purple-800">Total Hours</div>
            <div className="text-2xl font-bold text-purple-900">{summary.total_hours}</div>
          </div>
          <div className="card bg-orange-50 border-orange-200 p-4">
            <div className="text-sm font-medium text-orange-800">Extra Hours</div>
            <div className="text-2xl font-bold text-orange-900">{summary.extra_hours}</div>
          </div>
        </div>

        {/* Attendance Data Table */}
        {attendanceRecords.length > 0 ? (
          <div className="card">
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    {!isEmployee() && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clock In
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clock Out
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Extra Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {attendanceRecords.map((record, index) => {
                    const totalHours = calculateHours(record.clock_in, record.clock_out);
                    const extraHours = calculateExtraHours(totalHours);
                    
                    return (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {new Date(record.date).toLocaleDateString('en-GB')}
                        </td>
                        {!isEmployee() && (
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {record.first_name} {record.last_name}
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {record.department || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTimeDisplay(record.clock_in)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTimeDisplay(record.clock_out)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {totalHours > 0 ? `${totalHours} hrs` : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {extraHours > 0 ? `${extraHours} hrs` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`badge ${
                            record.status === 'present' ? 'badge-success' :
                            record.status === 'late' ? 'badge-warning' :
                            record.status === 'absent' ? 'badge-danger' :
                            'badge-gray'
                          }`}>
                            {record.status || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="card text-center py-12">
            <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Attendance Data</h3>
            <p className="text-gray-600">No attendance records found for the selected filters.</p>
          </div>
        )}
      </div>
    );
  };

  const renderFilters = () => {
    return (
      <div className="card">
        <div className="flex items-center mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Filter Options</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              className="form-input"
              value={attendanceFilters.start_date}
              onChange={(e) => setAttendanceFilters({
                ...attendanceFilters,
                start_date: e.target.value
              })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              className="form-input"
              value={attendanceFilters.end_date}
              onChange={(e) => setAttendanceFilters({
                ...attendanceFilters,
                end_date: e.target.value
              })}
            />
          </div>

          {/* Department */}
          {!isEmployee() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Department
              </label>
              <select
                className="form-input"
                value={attendanceFilters.department}
                onChange={(e) => setAttendanceFilters({
                  ...attendanceFilters,
                  department: e.target.value
                })}
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id} value={dept.name}>{dept.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Employee */}
          {!isEmployee() && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Employee
              </label>
              <select
                className="form-input"
                value={attendanceFilters.employee_id}
                onChange={(e) => setAttendanceFilters({
                  ...attendanceFilters,
                  employee_id: e.target.value
                })}
              >
                <option value="">All Employees</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.first_name} {emp.last_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              className="form-select"
              value={attendanceFilters.status}
              onChange={(e) => setAttendanceFilters({
                ...attendanceFilters,
                status: e.target.value
              })}
            >
              <option value="">All Statuses</option>
              <option value="present">Present</option>
              <option value="absent">Absent</option>
              <option value="late">Late</option>
            </select>
          </div>

          {/* Generate Button */}
          <div className="flex items-end">
            <button
              onClick={fetchAttendanceReport}
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Report'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEarlyCheckinFilters = () => {
    return (
      <div className="card">
        <div className="flex items-center mb-4">
          <FunnelIcon className="w-5 h-5 text-gray-500 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">Early Check-in Filter Options</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              className="form-input"
              value={earlyCheckinFilters.start_date}
              onChange={(e) => setEarlyCheckinFilters({
                ...earlyCheckinFilters,
                start_date: e.target.value
              })}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              className="form-input"
              value={earlyCheckinFilters.end_date}
              onChange={(e) => setEarlyCheckinFilters({
                ...earlyCheckinFilters,
                end_date: e.target.value
              })}
            />
          </div>

          {/* Department Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Department
            </label>
            <select
              className="form-input"
              value={earlyCheckinFilters.department}
              onChange={(e) => setEarlyCheckinFilters({
                ...earlyCheckinFilters,
                department: e.target.value
              })}
            >
              <option value="">All Departments</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* Employee Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee
            </label>
            <select
              className="form-input"
              value={earlyCheckinFilters.employee_id}
              onChange={(e) => setEarlyCheckinFilters({
                ...earlyCheckinFilters,
                employee_id: e.target.value
              })}
            >
              <option value="">All Employees</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.first_name} {emp.last_name} ({emp.employee_number})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={generateEarlyCheckinReport}
            disabled={loading}
            className="btn-primary"
          >
            {loading ? 'Generating...' : 'Generate Early Check-in Report'}
          </button>
        </div>
      </div>
    );
  };

  const renderEarlyCheckinReport = () => {
    if (!reportData) {
      return (
        <div className="card text-center py-12">
          <ExclamationTriangleIcon className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Generate Early Check-in Report</h3>
          <p className="text-gray-600">Select your filters and click "Generate Report" to view early check-in data.</p>
        </div>
      );
    }

    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Early Check-in Report Results</h3>
          <span className="text-sm text-gray-600">
            {reportData.length} record(s) found
          </span>
        </div>
        
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Date</th>
                <th>Check-in Time</th>
                <th>Official Start</th>
                <th>Early By</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {reportData.map((record, index) => (
                <tr key={index}>
                  <td>
                    <div>
                      <div className="font-medium text-gray-900">
                        {record.first_name} {record.last_name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {record.employee_number}
                      </div>
                    </div>
                  </td>
                  <td>{record.department || '-'}</td>
                  <td>{new Date(record.date).toLocaleDateString()}</td>
                  <td className="font-medium text-blue-600">{record.check_in}</td>
                  <td>{record.official_start_time}</td>
                  <td className="font-medium text-amber-600">{record.early_by}</td>
                  <td>
                    <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                      Early Check-in
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const generateEarlyCheckinReport = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = { ...earlyCheckinFilters };
      console.log('Generating early check-in report with filters:', params);
      
      const response = await reportsAPI.getEarlyCheckin(params);
      setReportData(response.data.early_checkin_data || []);
    } catch (error) {
      console.error('Error generating early check-in report:', error);
      setError(error.response?.data?.message || 'Failed to generate early check-in report');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'attendance':
        return (
          <div className="space-y-6">
            {renderFilters()}
            {renderAttendanceReport()}
          </div>
        );
      case 'early-checkin':
        return (
          <div className="space-y-6">
            {renderEarlyCheckinFilters()}
            {renderEarlyCheckinReport()}
          </div>
        );
      case 'payroll':
        return (
          <div className="card text-center py-12">
            <DocumentArrowDownIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Payroll Report</h3>
            <p className="text-gray-600">Payroll reporting functionality coming soon.</p>
          </div>
        );
      case 'leave':
        return (
          <div className="card text-center py-12">
            <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Leave Report</h3>
            <p className="text-gray-600">Leave reporting functionality coming soon.</p>
          </div>
        );
      case 'employee':
        return (
          <div className="card text-center py-12">
            <UserIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Employee Report</h3>
            <p className="text-gray-600">Employee reporting functionality coming soon.</p>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Reports & Analytics</h1>
          <p className="text-sm text-gray-600 mt-1">Generate and analyze organizational reports</p>
        </div>
        {activeTab === 'attendance' && reportData && (
          <div className="flex space-x-3">
            <button
              onClick={() => handleExportReport('csv')}
              className="btn-secondary"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export CSV
            </button>
            <button
              onClick={() => handleExportReport('json')}
              className="btn-secondary"
            >
              <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
              Export JSON
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="card">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {availableTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`nav-item py-4 px-1 border-b-2 font-medium flex items-center space-x-2 ${
                    activeTab === tab.id
                      ? 'nav-item-active border-blue-500 text-blue-600'
                      : 'nav-item-inactive border-transparent'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
        
        <div className="p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            renderTabContent()
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
